import axios from 'axios';
import { auth } from '../config/firebase';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor
api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Team {
  id: number;
  name: string;
  shortName: string;
  crest: string;
}

export interface Match {
  id: number;
  utcDate: string;
  status: string;
  matchday: number;
  homeTeam: Team;
  awayTeam: Team;
  competition: {
    name: string;
    emblem: string;
  };
  venue?: string;
}

export const premierLeagueService = {
  // Get competitions list
  async getCompetitions() {
    try {
      const response = await api.get('/premier-league/competitions');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching competitions:', error);
      return [];
    }
  },

  // Get matches with optional competition filter
  async getMatches(
    dateFrom?: string,
    dateTo?: string,
    competitions?: string[]
  ): Promise<Match[]> {
    try {
      const params: any = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (competitions && competitions.length > 0) {
        params.competitions = competitions.join(',');
      }

      const response = await api.get('/premier-league/matches', { params });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching matches:', error);
      return [];
    }
  },

  // Search matches by team name
  async searchMatches(query: string): Promise<Match[]> {
    try {
      const allMatches = await this.getMatches();
      const searchLower = query.toLowerCase();

      return allMatches.filter(
        (match) =>
          match.homeTeam.name.toLowerCase().includes(searchLower) ||
          match.awayTeam.name.toLowerCase().includes(searchLower) ||
          match.homeTeam.shortName.toLowerCase().includes(searchLower) ||
          match.awayTeam.shortName.toLowerCase().includes(searchLower)
      );
    } catch (error) {
      console.error('Error searching matches:', error);
      return [];
    }
  },

  // Get upcoming matches with optional competition filter
  async getUpcomingMatches(competitions?: string[]): Promise<Match[]> {
    try {
      const params: any = {};
      if (competitions && competitions.length > 0) {
        params.competitions = competitions.join(',');
      }
      const response = await api.get('/premier-league/matches/upcoming', {
        params,
      });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching upcoming matches:', error);
      return [];
    }
  },

  // Get European competition matches
  async getEuropeanMatches(
    dateFrom?: string,
    dateTo?: string
  ): Promise<Match[]> {
    try {
      const params: any = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const response = await api.get('/premier-league/matches/european', {
        params,
      });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching European matches:', error);
      return [];
    }
  },

  // Get all major competition matches (PL + European)
  async getAllMajorMatches(
    dateFrom?: string,
    dateTo?: string
  ): Promise<Match[]> {
    try {
      const params: any = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const response = await api.get('/premier-league/matches/all-major', {
        params,
      });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching all major matches:', error);
      return [];
    }
  },

  // Get all season matches
  async getSeasonMatches(): Promise<Match[]> {
    try {
      const response = await api.get('/premier-league/matches/season');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching season matches:', error);
      return [];
    }
  },

  // Get all teams
  async getTeams(): Promise<Team[]> {
    try {
      const response = await api.get('/premier-league/teams');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching teams:', error);
      return [
        {
          id: 57,
          name: 'Arsenal FC',
          shortName: 'Arsenal',
          crest: 'https://crests.football-data.org/57.png',
        },
        {
          id: 61,
          name: 'Chelsea FC',
          shortName: 'Chelsea',
          crest: 'https://crests.football-data.org/61.png',
        },
        {
          id: 64,
          name: 'Liverpool FC',
          shortName: 'Liverpool',
          crest: 'https://crests.football-data.org/64.png',
        },
        {
          id: 65,
          name: 'Manchester City FC',
          shortName: 'Man City',
          crest: 'https://crests.football-data.org/65.png',
        },
        {
          id: 66,
          name: 'Manchester United FC',
          shortName: 'Man United',
          crest: 'https://crests.football-data.org/66.png',
        },
        {
          id: 73,
          name: 'Tottenham Hotspur FC',
          shortName: 'Tottenham',
          crest: 'https://crests.football-data.org/73.png',
        },
      ];
    }
  },

  // Get La Liga teams
  async getLaLigaTeams(): Promise<Team[]> {
    try {
      const response = await api.get('/premier-league/teams/la-liga');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching La Liga teams:', error);
      return [];
    }
  },

  // Get Bundesliga teams
  async getBundesligaTeams(): Promise<Team[]> {
    try {
      const response = await api.get('/premier-league/teams/bundesliga');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching Bundesliga teams:', error);
      return [];
    }
  },

  // Get Serie A teams
  async getSerieATeams(): Promise<Team[]> {
    try {
      const response = await api.get('/premier-league/teams/serie-a');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching Serie A teams:', error);
      return [];
    }
  },

  // Get Ligue 1 teams
  async getLigue1Teams(): Promise<Team[]> {
    try {
      const response = await api.get('/premier-league/teams/ligue-1');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching Ligue 1 teams:', error);
      return [];
    }
  },

  // Get EFL Championship teams
  async getEFLChampionshipTeams(): Promise<Team[]> {
    try {
      const response = await api.get('/premier-league/teams/efl-championship');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching EFL Championship teams:', error);
      return [];
    }
  },

  // Get all major league teams
  async getAllMajorLeagueTeams(): Promise<Team[]> {
    try {
      const response = await api.get('/premier-league/teams/all-major');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching all major league teams:', error);
      return [];
    }
  },

  // Get all matches for a specific team
  async getTeamMatches(teamId: number): Promise<Match[]> {
    try {
      const response = await api.get(`/premier-league/teams/${teamId}/matches`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching team matches:', error);
      return [];
    }
  },
};

// Mock data for development/demo
function getMockMatches(): Match[] {
  const today = new Date();
  const teams = [
    {
      id: 57,
      name: 'Arsenal FC',
      shortName: 'Arsenal',
      crest: 'https://crests.football-data.org/57.png',
    },
    {
      id: 61,
      name: 'Chelsea FC',
      shortName: 'Chelsea',
      crest: 'https://crests.football-data.org/61.png',
    },
    {
      id: 64,
      name: 'Liverpool FC',
      shortName: 'Liverpool',
      crest: 'https://crests.football-data.org/64.png',
    },
    {
      id: 65,
      name: 'Manchester City FC',
      shortName: 'Man City',
      crest: 'https://crests.football-data.org/65.png',
    },
    {
      id: 66,
      name: 'Manchester United FC',
      shortName: 'Man United',
      crest: 'https://crests.football-data.org/66.png',
    },
    {
      id: 73,
      name: 'Tottenham Hotspur FC',
      shortName: 'Tottenham',
      crest: 'https://crests.football-data.org/73.png',
    },
  ];

  return Array.from({ length: 30 }, (_, index) => {
    const matchDate = new Date(today);
    matchDate.setDate(today.getDate() + index * 3);

    const homeTeamIndex = Math.floor(Math.random() * teams.length);
    let awayTeamIndex = Math.floor(Math.random() * teams.length);
    while (awayTeamIndex === homeTeamIndex) {
      awayTeamIndex = Math.floor(Math.random() * teams.length);
    }

    return {
      id: 1000 + index,
      utcDate: matchDate.toISOString(),
      status: 'SCHEDULED',
      matchday: index + 1,
      homeTeam: teams[homeTeamIndex],
      awayTeam: teams[awayTeamIndex],
      competition: {
        name: 'Premier League',
        emblem: 'https://crests.football-data.org/2021.png',
      },
      venue: `${teams[homeTeamIndex].name} Stadium`,
    };
  });
}
